import { Close, EditNote, History } from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import type React from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import TailwindSlider from "~/components/ui/slider";
import { TOAST_MESSAGES } from "~/constants/messages";
import { OrganizationApi } from "~/functions/api/organization";
import ReloadIcon from "~/images/reload.svg?react";
import { setOrganization } from "~/slices/auth";
import type { RootState } from "~/store";
import styles from "../styles.module.scss";

interface ChatHeaderProps {
  onClose: () => void;
  onHistory: () => void;
  onNewChat: () => void;
  timeoutMessage?: string;
  onReload?: () => void;
  remainingOffset?: number;
  onRemainingOffsetReset?: () => void;
}

const ChatHeader: React.FC<ChatHeaderProps> = ({
  onClose,
  onHistory,
  onNewChat,
  timeoutMessage,
  onReload,
  remainingOffset = 0,
  onRemainingOffsetReset,
}) => {
  const { organization } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();
  // AIチャット利用数の表示（残りリクエスト数 / 月間上限リクエスト数）
  // ローカルオフセットを加算して即時反映（Redux storeの値 + 送信ごとの-1オフセット）
  const remainingRequests = Math.max(0, organization.remainingChatRequests + remainingOffset);
  const monthlyRequestLimit = organization.plan.limits.monthlyRequestLimit;
  const planType = organization.plan.type;

  // 使用率を計算（0-100%）
  const usagePercentage =
    planType !== "free" ? (remainingRequests / monthlyRequestLimit) * 100 : 50;

  // 使用率に基づいて色を決定
  // 使用率が33%以下： 赤（上限に近い）
  // 使用率が33-66%：黄色（注意）
  // 使用率が66%以上：緑（まだ余裕がある）
  const getColor = (usagePercent: number): string => {
    if (usagePercent <= 33) return "#D61D1D"; // $object-success (使用量少ない)
    if (usagePercent <= 66) return "#D6C31D"; // $object-warning (使用量中程度)
    return "#4EBC44"; // $object-danger (使用量多い)
  };

  const statusColor = getColor(usagePercentage);

  const handleReload = async () => {
    // Tutorial path: parent handles reload
    if (onReload) {
      onReload();
      return;
    }

    // Default path: fetch from API and update store
    if (!organization?.id) {
      toast.error(TOAST_MESSAGES.AI_CHAT.ORGANIZATION_NOT_FOUND);
      return;
    }
    const organizationApi = new OrganizationApi();
    const updatedOrganization = await organizationApi.get(organization.id);
    if (updatedOrganization) {
      dispatch(setOrganization(updatedOrganization));
      // API から最新値を取得したのでローカルオフセットをリセット
      onRemainingOffsetReset?.();
    } else {
      toast.error(TOAST_MESSAGES.AI_CHAT.CREDIT_UPDATE_FAILED);
    }
  };

  return (
    <div className={styles["chatHeader"]}>
      <div className={styles["headerLeft"]}>
        <h2 className={styles["chatTitle"]}>AIチャット</h2>
        {timeoutMessage && <div className={styles["timeoutMessage"]}>{timeoutMessage}</div>}
        <div className={styles["creditInfo"]}>
          <div className={styles["creditNumbers"]}>
            <span className={styles["creditCurrent"]} style={{ color: statusColor }}>
              {remainingRequests.toLocaleString()}
            </span>
            <span className={styles["creditMax"]}>
              / {planType !== "free" ? monthlyRequestLimit.toLocaleString() : "50"}
            </span>
            <Tooltip title="更新" placement="top">
              <button
                className={`${styles["iconButton"]} ${styles["creditReloadButton"]}`}
                onClick={handleReload}
                aria-label="リクエスト数を更新"
              >
                <ReloadIcon />
              </button>
            </Tooltip>
          </div>
          <div className={styles["creditBar"]}>
            <div className={styles["creditBarRow"]}>
              <TailwindSlider
                value={remainingRequests}
                max={monthlyRequestLimit > 0 ? monthlyRequestLimit : 50}
                barColor={statusColor}
              />
            </div>
          </div>
        </div>
      </div>
      <div className={styles["headerActions"]}>
        <Tooltip title="履歴" placement="top">
          <button className={styles["iconButton"]} onClick={onHistory} aria-label="履歴を表示">
            <History />
          </button>
        </Tooltip>
        <Tooltip title="新規チャット" placement="top">
          <button className={styles["iconButton"]} onClick={onNewChat} aria-label="新規チャット">
            <EditNote />
          </button>
        </Tooltip>
        <button className={styles["iconButton"]} onClick={onClose} aria-label="閉じる">
          <Close />
        </button>
      </div>
    </div>
  );
};

export default ChatHeader;
